import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Tag } from 'antd';
import { forEach } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMedia } from 'react-use';
import { Active, EditButton, Inactive } from '../../../../../assets/svg';
import { BREAKPOINTS, SCROLL_CONST } from '../../../../../common/constants';
import { titleCase } from '../../../../../common/utils';
import CommonCard from '../../../../../components/CommonCard';
import CommonTable from '../../../../../components/CommonTable';
import AddEditUserModal from '../../../AddEditUserModal';
import DeleteModal from '../../../DeleteModal';
import { UPDATE_STATUS } from '../../../graphql/Mutations';
import { GET_ORGANIZATION_USERS } from '../../../graphql/Queries';

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setStatusModal] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [mutationId, setMutationId] = useState('');
  const [title, setTitle] = useState('');
  const [scrollFlag, setScrollFlag] = useState(false);
  const [userData, setUserData] = useState(null);
  const [newdata, setNewdata] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;
  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: limit
  };
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const { id } = useParams();

  const [updateStatus] = useMutation(UPDATE_STATUS, {
    onError() {},
    refetchQueries: [
      {
        query: GET_ORGANIZATION_USERS,
        variables: {
          filter: {
            tenantId: Number(id),
            sortBy: { field: 'updatedAt', order: 'DESC' }
          }
        }
      }
    ]
  });
  const handleActiveStatus = () => {
    setStatusModal(true);
  };
  const ActiveButtons = ({ record }) => {
    if (record?.isDisabled === false) {
      return (
        <Inactive
          onClick={() => {
            setMutationId(record?.id);
            setIsDisabled(true);
            handleActiveStatus();
            setTitle('Deactivate');
          }}
        />
      );
    }
    return (
      <Active
        onClick={() => {
          setMutationId(record?.id);
          setIsDisabled(true);
          handleActiveStatus();
          setTitle('Activate');
        }}
      />
    );
  };
  const ChangeStatus = ({ record }) => {
    if (record?.isDisabled === false) {
      return <Tag className="active-tag">Active</Tag>;
    }

    return <Tag className="inactive-tag">Inactive</Tag>;
  };

  const [fetchUsers] = useLazyQuery(GET_ORGANIZATION_USERS, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const pagination = {
        ...paginationProp,
        defaultPageSize: 10,
        total: res?.tenantUserList?.total
      };
      setPaginationProp(pagination);
      setHasMore(!!res?.tenantUserList?.data?.length);
      if (scrollFlag) {
        const datalist = [...newdata];
        forEach(res?.tenantUserList?.data, (item) => datalist.push(item));
        setNewdata(datalist);
        setScrollFlag(false);
      } else {
        const datalist = [];
        forEach(res?.tenantUserList?.data, (item) => datalist.push(item));
        setNewdata(datalist);
      }
      setLoading(false);
    },
    onError() {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchUsers({
      variables: {
        filter: {
          skip: 0,
          limit: limit,
          tenantId: Number(id),
          sortBy: { field: 'updatedAt', order: 'DESC' }
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollFlag) {
      fetchUsers({
        variables: {
          filter: {
            skip: newdata.length,
            limit: limit,
            tenantId: Number(id),
            sortBy: { field: 'updatedAt', order: 'DESC' }
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollFlag]);

  const handleAddEditUser = (record) => {
    if (record) {
      setUserData(record);
    } else {
      setUserData();
    }
    setShowModal(true);
  };
  const handleScroll = (e) => {
    const { target } = e;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};
    const scrolledToBottom =
      scrollTop + offsetHeight >= scrollHeight - SCROLL_CONST;

    if (scrolledToBottom && hasMore && !loading && !scrollFlag) {
      setScrollFlag(true);
      fetchUsers({
        variables: {
          filter: {
            tenantId: Number(id),
            skip: newdata.length,
            limit: limit,
            sortBy: { field: 'updatedAt', order: 'DESC' }
          }
        }
      });
    }
  };
  const handleTableChange = (pagination) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setPaginationProp({ ...paginationProp, ...pagination });
    fetchUsers({
      variables: {
        filter: {
          tenantId: Number(id),
          skip,
          limit: pagination?.pageSize,
          sortBy: { field: 'updatedAt', order: 'DESC' }
        }
      }
    });
  };
  const columns = [
    {
      title: 'ID',
      key: 'id',
      render: (text, record, index) => {
        return (
          <>
            {(paginationProp?.current - 1) * paginationProp?.pageSize +
              index +
              1}
          </>
        );
      }
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'organizationName'
    },
    {
      title: 'ROLE',
      dataIndex: 'roles',
      key: 'users',
      render: (text) => titleCase(text)
    },
    {
      title: 'EMAIL ID',
      dataIndex: 'email',
      key: 'adminEmail'
    },
    {
      title: 'STATUS',
      dataIndex: '',
      key: 'isDisabled',
      render: (status, record) => {
        if (record?.isDisabled === false) {
          return <Tag className="active-tag">Active</Tag>;
        }
        return <Tag className="inactive-tag">Inactive</Tag>;
      }
    },
    {
      title: '',
      key: 'Action',
      render: (text, record) => {
        return (
          <div className="action">
            <Button
              shape="round"
              icon={<EditButton />}
              onClick={() => handleAddEditUser(record)}
            />
            <Button shape="round" icon={<ActiveButtons record={record} />} />
          </div>
        );
      }
    }
  ];
  return (
    <div className="user-details">
      {showModal && (
        <AddEditUserModal
          showModal={showModal}
          setShowModal={setShowModal}
          userData={userData}
          isUpdate={!!userData}
          fetchUsers={() => {
            setPaginationProp(initialPaginationValue);
            fetchUsers({
              variables: {
                filter: {
                  skip: 0,
                  limit: limit,
                  tenantId: Number(id),
                  sortBy: { field: 'updatedAt', order: 'DESC' }
                }
              }
            });
          }}
        />
      )}
      {showStatusModal && (
        <DeleteModal
          showModal={showStatusModal}
          setShowModal={setStatusModal}
          title={title}
          updateUserStatus={updateStatus}
          mutationId={mutationId}
          setMutationId={setMutationId}
          isDisabled={isDisabled}
          fetchUserData={() => {
            setPaginationProp(initialPaginationValue);
            fetchUsers({
              variables: {
                filter: {
                  skip: 0,
                  limit: limit,
                  tenantId: Number(id),
                  sortBy: { field: 'updatedAt', order: 'DESC' }
                }
              }
            });
          }}
          id={id}
          name="User"
          subtitle="User"
        />
      )}
      <div className="users-header">
        <h1 className="users-header-title">Tenant Users</h1>

        <Button
          type="primary"
          shape="round"
          onClick={() => handleAddEditUser()}
        >
          {isDesktopViewport ? 'Add User' : 'Add'}
        </Button>
      </div>
      {isDesktopViewport ? (
        <div className="user-table pointer">
          <CommonTable
            loadingData={loading}
            columns={columns}
            data={newdata}
            rowKey={(obj) => obj?.id}
            onChange={handleTableChange}
            paginationConfig={paginationProp}
          />
        </div>
      ) : (
        <div className="users-card pointer" onScroll={handleScroll}>
          {newdata?.map((data) => {
            return (
              <div key={data?.id}>
                <CommonCard>
                  <div>
                    <div className="card-header">
                      <h1 className="card-header-h1">{data?.name}</h1>
                      <span>
                        <ChangeStatus record={data} />
                      </span>
                    </div>
                    <div className="card-content">
                      <br />
                      <p>{titleCase(data?.roles)}</p>
                      <div className="d-flex justify-between align-center flex-wrap">
                        <span className="mr-5 word-break">{data?.email}</span>
                        <span className="action d-flex">
                          <Button
                            shape="round"
                            icon={<EditButton />}
                            onClick={() => handleAddEditUser(data)}
                          />
                          <Button
                            shape="round"
                            icon={<ActiveButtons record={data} />}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </CommonCard>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Users;
